import { withUrqlClient, NextUrqlAppContext, NextUrqlClientConfig } from 'next-urql';
import { withUrqlSsr } from 'lib/urql';
import { Provider } from 'react-redux';
import { useClientStore, createServerStore } from '../lib/redux';
import BaseLayout from '../app/components/App/BaseLayout';
import BaseHtmlHeader from '../app/components/App/BaseHtmlHeader';
import NextApp, { AppProps, NextWebVitalsMetric } from 'next/app';
import '../app/components/App/normalize.css';
import setupAbiskoServer from 'lib/abiskoServer';
import setupAbiskoClient from '../client/abisko';
import React, { useEffect, useState } from 'react';
import { fromJS } from 'immutable';
import { loadPersistentActions } from 'app/utils/persistentState';
import { Debug } from '@svt/debug';
import { Store } from 'redux';
import { fetchParentalLock } from 'app/actions/parentalLock';
import { useRouter } from 'next/router';
import { SlashTiming } from 'app/ui-components/design-tokens';
import VideoModal from 'app/components/VideoModal/VideoModal';
import { usePrevious } from 'app/hooks/usePreviousHook';
import { UserContextProvider } from 'app/context/UserContext';
import { reportWebVitalsToHelix } from 'app/utils/statistics/performanceUtils';

const { error: errorLogger } = Debug('play:client:app:error');
interface Props extends AppProps {
    initialStore: object;
    reduxStore: Store<any, any>;
}

const DelayedUnmount = ({ shouldDelay, children }) => {
    const [displayChildren, setDisplayChildren] = useState(children);

    useEffect(() => {
        let timeout;

        if (shouldDelay) {
            timeout = setTimeout(() => {
                setDisplayChildren(children);
            }, SlashTiming * 1.1);
        } else {
            setDisplayChildren(children);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [shouldDelay, children]);

    return shouldDelay ? displayChildren : children;
};

const ScrollRestore = ({ videoModalOpen, children }) => {
    const previousVideoModalOpen = usePrevious(videoModalOpen);

    // Restore scroll pos after closing video modal
    useEffect(() => {
        let scrollRaf = null;
        if (previousVideoModalOpen && !videoModalOpen) {
            try {
                const value = JSON.parse(
                    window.sessionStorage.getItem(`__next_scroll_${window.history?.state?.idx}`)
                );

                if (value?.y) {
                    scrollRaf = requestAnimationFrame(() => {
                        window.scrollTo(value.x, value.y);
                    });
                }
            } catch (e) {
                console.log('error restoring scroll pos', e); // eslint-disable-line
            }
        }

        return () => {
            if (scrollRaf) {
                cancelAnimationFrame(scrollRaf);
            }
        };
    }, [previousVideoModalOpen, videoModalOpen]);

    return children;
};

const App = ({ Component, pageProps, initialStore, reduxStore }: Props) => {
    const store = useClientStore(fromJS(initialStore), reduxStore);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Enable this again when ContinueWatchingMigration is removed
            loadPersistentActions(store.dispatch);
            setupAbiskoClient(store);
            store.dispatch(fetchParentalLock());
        }
    }, []); // eslint-disable-line

    const router = useRouter();
    const videoModalOpen = Boolean(router.query.modalId);

    const RenderComponent = videoModalOpen ? React.Fragment : Component;

    return (
        <>
            <BaseHtmlHeader />
            <Provider store={store}>
                <UserContextProvider>
                    <BaseLayout>
                        <DelayedUnmount shouldDelay={router.query.delayUnmount}>
                            <ScrollRestore videoModalOpen={videoModalOpen}>
                                <RenderComponent {...pageProps} />
                            </ScrollRestore>
                        </DelayedUnmount>
                        {videoModalOpen && <VideoModal id={String(router.query.modalId)} />}
                    </BaseLayout>
                </UserContextProvider>
            </Provider>
        </>
    );
};

App.getInitialProps = async (ctx: NextUrqlAppContext) => {
    const store = createServerStore(undefined);
    const appProps = await NextApp.getInitialProps(ctx);

    const req = ctx.ctx.req as any; // needed due to reduxStore
    if (req) {
        try {
            await setupAbiskoServer(store, req);
        } catch (e) {
            errorLogger('error abisko', e);
        }

        req.reduxStore = store;
    }

    return {
        ...appProps,
        initialStore: store.getState().toJS(),
        reduxStore: store,
    };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
    reportWebVitalsToHelix(metric);
}

export default withUrqlClient(withUrqlSsr as NextUrqlClientConfig)(App as any); // needed due to reduxStore
